import { createContext, useContext, type ReactNode, type FC } from 'react';
import { type SupportedCurrencies } from 'dibs-currency-helpers/exports/currencies.ts';

type HomePageContextType = {
    isBot: boolean;
    isDesktop: boolean;
    isMobile: boolean;
    isTablet: boolean;
    isLoggedInUser: boolean;
    userType: string;
    homepageId: string;
    priceBookName: string;
    isTrade: boolean;
    serverCurrency?: SupportedCurrencies;
    isTopDisplayAdEnabled?: boolean;
    isBottomDisplayAdEnabled?: boolean;
    isRemoveTopHeroBannerTestVariant?: boolean;
    isPersonalizedContentAboveFoldTestVariant?: boolean;
};

const HomePageContext = createContext<HomePageContextType>({
    isBot: false,
    isDesktop: false,
    isMobile: false,
    isTablet: false,
    isLoggedInUser: false,
    homepageId: '',
    priceBookName: 'DEFAULT',
    isTrade: false,
    serverCurrency: 'USD',
    userType: '',
    isTopDisplayAdEnabled: false,
    isBottomDisplayAdEnabled: false,
    isRemoveTopHeroBannerTestVariant: false,
    isPersonalizedContentAboveFoldTestVariant: false,
});

type ServerVarsProviderProps = {
    children: ReactNode;
} & HomePageContextType;

export const useHomePageContext = (): HomePageContextType => {
    const context = useContext(HomePageContext);
    if (!context) {
        throw new Error('useHomePageContext must be used within a HomePageContextProvider');
    }
    return context;
};

export const HomePageContextProvider: FC<ServerVarsProviderProps> = ({
    children,
    isBot,
    isDesktop,
    isMobile,
    isTablet,
    isLoggedInUser,
    userType,
    homepageId,
    priceBookName,
    isTrade,
    serverCurrency = 'USD',
    isTopDisplayAdEnabled,
    isBottomDisplayAdEnabled,
    isRemoveTopHeroBannerTestVariant,
    isPersonalizedContentAboveFoldTestVariant,
}) => {
    return (
        <HomePageContext
            value={{
                isBot,
                isMobile,
                isTablet,
                isDesktop,
                isLoggedInUser,
                userType,
                homepageId,
                priceBookName,
                isTrade,
                serverCurrency,
                isTopDisplayAdEnabled,
                isBottomDisplayAdEnabled,
                isRemoveTopHeroBannerTestVariant,
                isPersonalizedContentAboveFoldTestVariant,
            }}
        >
            {children}
        </HomePageContext>
    );
};
